(ns io.daskul.webapp.widgets.billing.line-editor
  "Unified invoice line editor (#843) — one shared, controlled component used by
   both the New Invoice page (folds a local `lines` vector, submits all at once)
   and the invoice detail page (wires callbacks to per-row /invoice-line-items
   CRUD). Keeping a single component is the anti-drift contract: rich editing
   must not fork between create and detail (the #831/#832/#835/#838 bug class).

   Controlled over a `lines` vector + callbacks:
     :lines      - vector of line maps (see line-editor-logic)
     :available  - excluded enrollment lines, shown in the Available tray
     :on-edit    - (fn [line-id patch-map]) merge a field change into a line
     :on-remove  - (fn [line-id]) drop a manual line / move an enrollment line
                   to :available
     :on-include - (fn [line-id]) re-include an available enrollment line
     :on-add     - (fn [kind]) append a blank line of :charge (or :credit)

   Layout follows the approved mockup: a line-item table
   (Description · Qty · Unit price · Amount · ✕) with Add-charge below, an
   Available-billables tray under that, and a totals block bottom-right. Later
   slices add the discount sub-line, credits, and the beneficiary picker."
  (:require
   [clojure.string :as str]
   [uix.core :as uix :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.format :as format]
   [io.daskul.webapp.widgets.billing.line-editor-logic :as logic]))

(def ^:private format-currency format/format-currency)

(defn- ->qty
  "Coerce a NumberInput value to a positive quantity, or nil when blank/invalid —
   a cleared field stays empty (and fails required-validation on Save) rather than
   snapping back to a value the user didn't type.

   A fractional entry is passed through, not floored: displaying 12.7 while
   charging for 12 is the corruption we are removing (#969). Validation rejects it."
  [v]
  (let [n (format/parse-number v)]
    (when (and n (pos? n)) n)))

(defn- fmt-amount
  "Currency with a leading minus for negatives (credits): −₱1,000.00."
  [n]
  (if (neg? n) (str "−" (format-currency (- n))) (format-currency n)))

(defn- discount-label
  "Human label for a line's discount — the note (reason), else the humanised
   rule type, else a generic fallback. A one-off's \"manual\" type is not shown
   as a label; without a reason it reads simply as \"Discount\"."
  [line matched]
  (or (:discount-note line) (:discount-note matched)
      (let [t (or (:discount-type line) (:discount-type matched))]
        (when (and t (not= t "manual"))
          (-> t (str/replace "-" " ") str/capitalize)))
      "Discount"))

(defn- action-link [label on-click]
  ($ ui/anchor {:component "button" :type "button"
                :style {:cursor "pointer"} :onClick on-click}
    label))

(defui beneficiary-pill
  "A subtle pill marking which student a line is for (enrollment lines)."
  [{:keys [name]}]
  ($ ui/badge {:variant "light" :color "blue" :size "sm" :radius "sm"
               :tt "none" :fw 500
               ;; hug content inside the flex-column stacks (card / read-only cell)
               :style {:alignSelf "flex-start"}
               :leftSection ($ icons/icon-user {:size 12})}
    name))

(defui discount-editor
  "The inline add/edit form for a line's discount (amount + optional reason).
   Spans the row; a one-off saves with no rule id and is written only on the line."
  [{:keys [line matched on-edit on-close]}]
  (let [line-id (:line-id line)
        cur (:discount-amount line)
        matched-amount (:discount-amount matched)
        [draft set-draft!] (uix/use-state (or cur 0))
        [reason set-reason!] (uix/use-state (or (:discount-note line) ""))
        save! (fn []
                (let [amt (format/parse-number draft)
                      note (let [r (str/trim (or reason ""))] (when (seq r) r))]
                  (on-edit line-id
                           (cond
                             (not (pos? amt))
                             {:discount-amount 0 :discount-type nil
                              :discount-note nil :discount-id nil}
                             (and matched (== amt matched-amount)
                                  (= note (:discount-note matched)))
                             matched
                             :else
                             {:discount-amount amt :discount-type "manual"
                              :discount-note note :discount-id nil}))
                  (on-close)))]
    ($ ui/box {:style {:backgroundColor "var(--mantine-color-gray-0)"
                       :border "1px solid var(--mantine-color-gray-3)"
                       :borderRadius 8 :padding "12px 14px 16px"}}
      ($ ui/stack {:gap 10}
        ($ ui/text {:fw 600} (str "Discount · " (:description line)))
        ($ ui/group {:gap "sm" :wrap "nowrap" :align "flex-end"}
          ($ ui/number-input
            {:prefix "₱" :thousandSeparator "," :hideControls true :min 0
             :w 140 :value draft :label "Amount off" :placeholder "0.00"
             :aria-label "Discount amount" :autoFocus true
             :onKeyDown (fn [e] (when (= (.-key e) "Enter") (save!)))
             :onChange #(set-draft! %)})
          ($ ui/text-input
            {:style {:flex 1} :value reason
             :label "Reason (optional)" :placeholder "e.g. Loyalty, one-time waiver"
             :aria-label "Discount reason"
             :onKeyDown (fn [e] (when (= (.-key e) "Enter") (save!)))
             :onChange #(set-reason! (.. % -target -value))})
          ($ ui/button {:variant "light" :onClick save!} "Save")
          ($ ui/button {:variant "subtle" :color "gray" :onClick on-close} "Cancel"))))))

(defui discount-controls
  "The per-line discount UI — label + −₱ figure + Edit/Remove/Reset (or Add
   discount), and the inline editor. Layout-neutral (a full-width group), so the
   table's discount row and the mobile line card share it. Auto-matched discounts
   pre-fill; Edit → a one-off (no rule id); Remove clears; Reset / Add back
   restores the match."
  [{:keys [line matched on-edit]}]
  (let [line-id (:line-id line)
        cur (:discount-amount line)
        matched-amount (:discount-amount matched)
        state (logic/discount-state {:current-amount cur :matched-amount matched-amount})
        [editing? set-editing!] (uix/use-state false)
        remove! #(on-edit line-id {:discount-amount 0 :discount-type nil
                                   :discount-note nil :discount-id nil})
        reset! #(on-edit line-id matched)
        active? (contains? #{:auto :custom} state)]
    (if editing?
      ($ discount-editor {:line line :matched matched :on-edit on-edit
                          :on-close #(set-editing! false)})
      ($ ui/group {:justify "space-between" :wrap "nowrap" :gap "sm" :align "center"
                   :style {:width "100%"}}
        ($ ui/group {:gap 8 :wrap "wrap" :align "center"}
          (case state
            :none    (action-link "＋ Add discount" #(set-editing! true))
            :removed ($ :<>
                       ($ ui/text {:c "dimmed"}
                         (str (discount-label line matched) " (removed)"))
                       (action-link "Add back" reset!))
            ($ :<>
              ($ ui/text {:c "dimmed"} (discount-label line matched))
              (action-link "Edit" #(set-editing! true))
              (action-link "Remove" remove!)
              (when (and matched (= state :custom))
                (action-link "Reset" reset!)))))
        (when active?
          ($ ui/text {:fw 500 :c "green"
                      :style {:whiteSpace "nowrap" :fontVariantNumeric "tabular-nums"}}
            (str "−" (format-currency cur))))))))

(defui discount-row
  "Table wrapper for discount-controls — spans Description…Amount (colSpan 4) so
   the space-between figure right-aligns to the Amount column's edge, level with
   the line's amount above it; the action column is left as an empty spacer."
  [{:keys [line matched on-edit]}]
  ($ ui/tr
    ($ ui/td {:colSpan 4 :style {:paddingTop 8 :paddingBottom 16}}
      ($ discount-controls {:line line :matched matched :on-edit on-edit}))
    ($ ui/td {:style {:width 40}})))

(defui credit-row
  "A credit line: a reason + a positive amount stored as a negative unit-price.
   Its own group row (keeps the separator border); no discount row."
  [{:keys [line on-remove on-edit invalid-fields]}]
  (let [{:keys [line-id description quantity unit-price]} line
        gross (logic/line-subtotal line)
        cell {:paddingTop 16 :paddingBottom 16 :verticalAlign "middle"}]
    ($ ui/tr
      ($ ui/td {:style cell}
        ($ ui/text-input
          {:value (or description "")
           :placeholder "Reason for credit"
           :aria-label "Credit reason"
           :error (contains? invalid-fields :description)
           :onChange #(on-edit line-id {:description (.. % -target -value)})}))
      ($ ui/td {:style (merge cell {:width 88})}
        ($ ui/number-input
          {:value (or quantity "")
           :min 1
           :aria-label "Qty"
           :hideControls true
           :error (contains? invalid-fields :quantity)
           :onChange #(on-edit line-id {:quantity (->qty %)})}))
      ($ ui/td {:style (merge cell {:width 140})}
        ($ ui/number-input
          {:value (if (some? unit-price) (js/Math.abs unit-price) "")
           :aria-label "Credit amount"
           :hideControls true
           :prefix "−₱"
           :thousandSeparator ","
           :min 0
           :error (contains? invalid-fields :unit-price)
           :onChange #(on-edit line-id {:unit-price (some-> (format/parse-number %) -)})}))
      ($ ui/td {:style (merge cell {:textAlign "right" :whiteSpace "nowrap"
                                    :fontVariantNumeric "tabular-nums"})}
        ($ ui/text {:fw 500 :c "green"} (fmt-amount gross)))
      ($ ui/td {:style (merge cell {:width 40})}
        ($ ui/action-icon
          {:variant "subtle" :color "red" :aria-label "Remove line"
           :style {:opacity 0.6}
           :onClick #(on-remove line-id)}
          ($ icons/icon-trash {:size 16}))))))

(defui line-row
  "One editable line (description / qty / unit-price inputs, gross amount, remove
   control) plus its nested discount row. Credit lines defer to credit-row.
   Beneficiary is carried (hidden) on enrollment lines only — manual lines bill
   the payer directly, so they show no beneficiary control."
  [{:keys [line discounts on-edit on-remove invalid-fields enrollment-editable?]}]
  (if (logic/credit? line)
    ($ credit-row {:line line :on-edit on-edit :on-remove on-remove
                   :invalid-fields invalid-fields})
    (let [{:keys [line-id description quantity unit-price beneficiary-name]} line
          matched (logic/match-line-discount line discounts)
          gross (* (or quantity 1) (or unit-price 0))
          ;; Enrollment lines are auto-populated from a billable: qty + price are
          ;; locked (adjust via a discount/credit), unless a product opts in.
          locked? (and (= :enrollment (:kind line)) (not enrollment-editable?))
          ;; Space above the row and a tight gap below, into its sub-rows.
          ;; verticalAlign middle keeps the amount + ✕ centered against the
          ;; input boxes (the student hint lives in its own sub-row, so this
          ;; main row stays single-line and perfectly aligned).
          attached {:paddingTop 16 :paddingBottom 2 :verticalAlign "middle"}]
      ($ :<>
        ;; The group-separator border lives on the <tr>; drop it here so the
        ;; parent and its sub-rows read as one group with no line between.
        ($ ui/tr {:style {:borderBottom "none"}}
          ($ ui/td {:style attached}
            ($ ui/text-input
              {:value (or description "")
               :placeholder "Description"
               :aria-label "Description"
               :error (contains? invalid-fields :description)
               :onChange #(on-edit line-id {:description (.. % -target -value)})}))
          ($ ui/td {:style (merge attached {:width 88})}
            ($ ui/number-input
              {:value (or quantity "")
               :min 1
               :aria-label "Qty"
               :hideControls true
               :disabled locked?
               :error (contains? invalid-fields :quantity)
               :onChange #(on-edit line-id {:quantity (->qty %)})}))
          ($ ui/td {:style (merge attached {:width 140})}
            ($ ui/number-input
              {:value (or unit-price "")
               :aria-label "Unit price"
               :hideControls true
               :prefix "₱"
               :thousandSeparator ","
               :disabled locked?
               :error (contains? invalid-fields :unit-price)
               :onChange #(on-edit line-id {:unit-price (format/parse-number %)})}))
          ($ ui/td {:style (merge attached {:textAlign "right" :whiteSpace "nowrap"
                                            :fontVariantNumeric "tabular-nums"})}
            ($ ui/text {:fw 500} (format-currency gross)))
          ($ ui/td {:style (merge attached {:width 40})}
            ($ ui/action-icon
              {:variant "subtle" :color "red" :aria-label "Remove line"
               :style {:opacity 0.6}
               :onClick #(on-remove line-id)}
              ($ icons/icon-trash {:size 16}))))
        ;; Enrollment lines: the student this line is for — its own sub-row so
        ;; the main row's amount stays aligned. Manual lines carry no name.
        (when beneficiary-name
          ($ ui/tr {:style {:borderBottom "none"}}
            ($ ui/td {:colSpan 5 :style {:borderTop "none" :paddingTop 0 :paddingBottom 4}}
              ($ beneficiary-pill {:name beneficiary-name}))))
        ($ discount-row {:line line :matched matched :on-edit on-edit})))))

(defui line-card
  "Mobile layout for one line — a card with stacked, labelled fields (a 4-column
   money table can't not be cramped on a phone). Same callbacks as the table row."
  [{:keys [line discounts on-edit on-remove invalid-fields enrollment-editable?]}]
  (let [{:keys [line-id description quantity unit-price beneficiary-name]} line
        credit? (logic/credit? line)
        matched (logic/match-line-discount line discounts)
        locked? (and (= :enrollment (:kind line)) (not enrollment-editable?))
        amount (if credit? (logic/line-subtotal line) (* (or quantity 1) (or unit-price 0)))]
    ($ ui/card {:withBorder true :radius "sm" :padding "sm"}
      ($ ui/stack {:gap "sm"}
        ($ ui/group {:gap "xs" :wrap "nowrap" :align "flex-start"}
          ($ ui/text-input
            {:value (or description "")
             :placeholder (if credit? "Reason for credit" "Description")
             :aria-label "Description"
             :style {:flex 1}
             :error (contains? invalid-fields :description)
             :onChange #(on-edit line-id {:description (.. % -target -value)})})
          ($ ui/action-icon
            {:variant "subtle" :color "red" :aria-label "Remove line"
             :style {:opacity 0.6}
             :onClick #(on-remove line-id)}
            ($ icons/icon-trash {:size 16})))
        (when beneficiary-name
          ($ beneficiary-pill {:name beneficiary-name}))
        ($ ui/group {:grow true :wrap "nowrap" :align "flex-end"}
          ($ ui/number-input
            {:label "Qty" :value (or quantity "") :min 1 :hideControls true
             :disabled locked?
             :error (contains? invalid-fields :quantity)
             :onChange #(on-edit line-id {:quantity (->qty %)})})
          (if credit?
            ($ ui/number-input
              {:label "Amount off" :prefix "−₱" :thousandSeparator "," :hideControls true :min 0
               :value (if (some? unit-price) (js/Math.abs unit-price) "")
               :error (contains? invalid-fields :unit-price)
               :onChange #(on-edit line-id {:unit-price (some-> (format/parse-number %) -)})})
            ($ ui/number-input
              {:label "Unit price" :prefix "₱" :thousandSeparator "," :hideControls true
               :value (or unit-price "")
               :disabled locked?
               :error (contains? invalid-fields :unit-price)
               :onChange #(on-edit line-id {:unit-price (format/parse-number %)})})))
        ($ ui/group {:justify "space-between" :align "center"}
          ($ ui/text {:c "dimmed"} "Amount")
          ($ ui/text (cond-> {:fw 600 :style {:fontVariantNumeric "tabular-nums"}}
                       credit? (assoc :c "green"))
            (fmt-amount amount)))
        (when-not credit?
          ($ discount-controls {:line line :matched matched :on-edit on-edit}))))))

(defui available-tray
  "Enrollment billables the customer has but that are not on the invoice —
   removed rows land here (the billable set is fixed, never destroyed). One-tap
   Add re-includes a row."
  [{:keys [available on-include]}]
  (when (seq available)
    ($ ui/card {:withBorder true :padding "sm" :radius "sm"
                :style {:backgroundColor "var(--mantine-color-gray-0)"}}
      ($ ui/text {:c "dimmed" :fw 600 :tt "uppercase" :mb "xs"}
        "Available billables")
      ($ ui/stack {:gap 6}
        (for [line available]
          ($ ui/group {:key (:line-id line) :gap "sm" :wrap "nowrap" :align "center"}
            ($ ui/text {:c "dimmed" :style {:flex 1}}
              (:description line))
            ($ ui/text {:c "dimmed"
                        :style {:whiteSpace "nowrap" :fontVariantNumeric "tabular-nums"}}
              (format-currency (logic/line-subtotal line)))
            ($ ui/button
              {:size "compact-xs" :variant "light"
               :leftSection ($ icons/icon-plus {:size 14})
               :onClick #(on-include (:line-id line))}
              "Add")))))))

(defui totals-row
  "One right-aligned label + amount row in the totals block. Amounts share a
   fixed-width right-aligned column so they line up across rows."
  [{:keys [label amount accent? emphasis?]}]
  ($ ui/group {:justify "flex-end" :gap "lg" :wrap "nowrap"}
    ($ ui/text (cond-> {:c "dimmed"}
                 emphasis? (assoc :fw 700 :size "lg" :c "inherit"))
      label)
    ($ ui/text (cond-> {:style {:minWidth 130 :textAlign "right" :fontVariantNumeric "tabular-nums"}}
                 accent?   (assoc :c "green")
                 emphasis? (assoc :fw 700 :size "lg"))
      amount)))

(defui line-editor
  ;; :invalid — map of line-id → invalid field set, surfaced as red inputs (empty
  ;;   ⇒ nothing flagged; the parent fills it on a Save attempt).
  ;; :enrollment-editable? — per-product seam: when false (default) an enrollment
  ;;   line's qty + price are locked (auto-populated from the billable).
  [{:keys [lines available discounts on-edit on-remove on-include on-add
           invalid enrollment-editable?]}]
  (let [mobile? (ui/use-media-query "(max-width: 640px)")]
   ($ ui/stack {:gap "sm"}
    ;; Below ~640px the money table can't not be cramped, so reflow each line to
    ;; a stacked, labelled card (the approved #843 mobile design).
    (if mobile?
      ($ ui/stack {:gap "sm"}
        (for [line lines]
          ($ line-card {:key (:line-id line)
                        :line line
                        :discounts discounts
                        :on-edit on-edit
                        :on-remove on-remove
                        :invalid-fields (get invalid (:line-id line))
                        :enrollment-editable? enrollment-editable?})))
      ($ ui/box {:style {:overflowX "auto"}}
        ($ ui/table {}
          ($ ui/thead
            ($ ui/tr
              ($ ui/th "Description")
              ($ ui/th {:style {:width 88}} "Qty")
              ($ ui/th {:style {:width 140}} "Unit price")
              ($ ui/th {:style {:textAlign "right"}} "Amount")
              ($ ui/th {:style {:width 40}})))
          ($ ui/tbody
            (for [line lines]
              ($ line-row {:key (:line-id line)
                           :line line
                           :discounts discounts
                           :on-edit on-edit
                           :on-remove on-remove
                           :invalid-fields (get invalid (:line-id line))
                           :enrollment-editable? enrollment-editable?}))))))

    ($ ui/group {:gap "lg"}
      ($ ui/anchor
        {:component "button" :type "button"
         :onClick #(on-add :charge)
         :style {:cursor "pointer"}}
        "＋ Add charge")
      ($ ui/anchor
        {:component "button" :type "button"
         :onClick #(on-add :credit)
         :style {:cursor "pointer"}}
        "＋ Add credit"))

    ($ available-tray {:available available :on-include on-include})

    ($ ui/divider)
    (let [charges (logic/lines-charges lines)
          deductions (+ (logic/lines-discount-total lines)
                        (logic/lines-credit-total lines))
          total (logic/lines-total lines)]
      ($ ui/stack {:gap 6 :align "flex-end"}
        ($ totals-row {:label "Charges" :amount (format-currency charges)})
        (when (pos? deductions)
          ($ totals-row {:label "Discounts & credits"
                         :amount (str "−" (format-currency deductions)) :accent? true}))
        ($ ui/divider {:w 260})
        ($ totals-row {:label "Total" :amount (format-currency total) :emphasis? true}))))))
